import React from "react";
import { useHistory } from "react-router-dom";
import type { ListingSubcategory } from "../../../components/landing/ListingGrid";

// The design's three stay types, and the registry names they can appear under
const STAY_TYPE_ALIASES: Record<string, string[]> = {
  hotels: ["hotel", "hotels", "accommodation"],
  apartments: ["apartment", "apartments", "serviced apartment"],
  boutique: ["boutique", "boutique hotel", "boutique hotels"],
};

/**
 * Search link for one stay type. Filters by the live sub-category ID when the registry has a
 * matching name, otherwise falls back to the `category` param the previous page used.
 */
export function staySearchUrl(key: keyof typeof STAY_TYPE_ALIASES, subcategories: ListingSubcategory[] = []) {
  const aliases = STAY_TYPE_ALIASES[key] || [key];
  const match = subcategories.find(sub => aliases.includes(String(sub.name || "").toLowerCase()));
  return match
    ? `/workspaces-search?type=stay&categoryId=2&subCategoryId=${match.id}`
    : `/workspaces-search?type=stay&category=${key}`;
}

function Badge({ label, color }: { label: string; color?: string }) {
  return (
    <span
      className={`inline-block rounded-full px-[12px] py-1 text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-white ${
        color ? "" : "border-[1px] border-solid border-white/30 bg-white/20"
      }`}
      style={color ? { backgroundColor: color } : undefined}
    >
      {label}
    </span>
  );
}

type StayIntroProps = { subcategories?: ListingSubcategory[] };

export default function StayIntro({ subcategories = [] }: StayIntroProps) {
  const history = useHistory();

  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-[1440px] px-[16px] sm:px-8">
        <div className="grid grid-cols-1 items-end gap-6 lg:grid-cols-2 lg:gap-10">
          <div>
            <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">Stay</p>
            <h2 className="pt-[12px] text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
              Stay Somewhere That
              <br className="hidden sm:block" /> Fits Your Journey
            </h2>
          </div>
          <p className="text-[18px] leading-[29.25px] text-[#717182]">
            Wherever you're headed, find a place to stay that fits how and why you're travelling.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-[20px] pt-14 lg:grid-cols-3">
          <button
            type="button"
            onClick={() => history.push(staySearchUrl("hotels", subcategories))}
            className="relative min-h-[400px] overflow-hidden rounded-3xl text-left"
          >
            <img
              src="/figma-assets/stay/intro-hotels.jpg"
              alt="Hotels"
              className="absolute inset-0 size-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[rgba(10,10,10,0.8)] via-[rgba(10,10,10,0.2)] to-[rgba(10,10,10,0)]" />
            <div className="absolute inset-x-0 bottom-0 p-7">
              <Badge label="Hotels" color="#017bef" />
              <h3 className="pt-[12px] text-[24px] font-bold leading-8 text-white">Hotels</h3>
              <p className="pt-2 text-[14px] leading-[22.75px] text-white/65">
                Comfortable, reliable accommodation for business and leisure travel.
              </p>
            </div>
          </button>

          <div className="flex flex-col gap-[20px] lg:col-span-2">
            {[
              {
                key: "apartments",
                label: "Apartments",
                badgeColor: "#0062c0",
                image: "/figma-assets/stay/intro-apartments.jpg",
                shade: "rgba(10,10,10,0.75)",
                description: "Serviced apartments for extended stays, relocations and a home away from home.",
                key2: "apartments",
              },
              {
                key: "boutique",
                label: "Boutique Hotels",
                badgeColor: undefined,
                image: "/figma-assets/stay/intro-boutique.jpg",
                shade: "rgba(1,45,110,0.8)",
                description: "Unique, characterful properties for travellers looking for something different.",
                key2: "boutique",
              },
            ].map(tile => (
              <button
                key={tile.key}
                type="button"
                onClick={() => history.push(staySearchUrl(tile.key2, subcategories))}
                className="relative flex min-h-[190px] flex-1 items-center overflow-hidden rounded-3xl px-8 text-left"
              >
                <img src={tile.image} alt={tile.label} className="absolute inset-0 size-full object-cover" />
                <div
                  className="absolute inset-0"
                  style={{ backgroundImage: `linear-gradient(90deg, ${tile.shade} 0%, rgba(0,0,0,0) 100%)` }}
                />
                <div className="relative max-w-[320px]">
                  <Badge label={tile.label} color={tile.badgeColor} />
                  <h3 className="pt-[12px] text-[24px] font-bold leading-8 text-white">{tile.label}</h3>
                  <p className="pt-1 text-[14px] leading-5 text-white/65">{tile.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
